import { buildDeck, randomSeed, shuffle } from "./deck";
import {
  canAutoComplete,
  canMoveToFoundation,
  canMoveToTableau,
  canPickUpRun,
  foundationIndex,
  isWon,
} from "./rules";
import { TABLEAU_COUNT, type Card, type GameState, type MoveSource, type Suit } from "./types";

/**
 * Moteur Klondike : possede l'etat de la partie, applique les coups apres
 * validation (rules.ts) et tient la pile d'annulation (undo illimite, cahier
 * des charges §6). Aucune notion de rendu : le controleur (main.ts) lit `view`
 * apres chaque action et anime la difference.
 */

/** Ce qu'une action a produit — de quoi declencher les bons sons/animations. */
export interface ActionResult {
  /** L'action a ete acceptee (sinon, rien n'a change). */
  ok: boolean;
  /** Une carte du tableau s'est retournee face visible suite au coup. */
  flipped: boolean;
  /** La pioche a ete recyclee (defausse retournee pour reformer la pioche). */
  recycled: boolean;
  /** Nombre de cartes tirees de la pioche (action draw uniquement). */
  drawn: number;
  /** Le coup a pose une carte sur une fondation. */
  toFoundation: boolean;
}

const REFUSED: ActionResult = { ok: false, flipped: false, recycled: false, drawn: 0, toFoundation: false };

function accepted(partial: Partial<ActionResult> = {}): ActionResult {
  return { ok: true, flipped: false, recycled: false, drawn: 0, toFoundation: false, ...partial };
}

export interface EngineOptions {
  seed?: number;
  drawCount?: 1 | 3;
}

export class KlondikeEngine {
  private state: GameState;
  private history: GameState[] = [];

  constructor(options: EngineOptions = {}) {
    const seed = options.seed ?? randomSeed();
    const drawCount = options.drawCount ?? 3;
    const deck = shuffle(buildDeck(), seed);

    // Distribution : la colonne n recoit n+1 cartes, seule la derniere face
    // visible ; les 24 restantes forment la pioche.
    const tableau: Card[][] = [];
    let cursor = 0;
    for (let col = 0; col < TABLEAU_COUNT; col++) {
      const pile: Card[] = [];
      for (let i = 0; i <= col; i++) {
        const card = deck[cursor++]!;
        card.faceUp = i === col;
        pile.push(card);
      }
      tableau.push(pile);
    }
    const stock = deck.slice(cursor);

    this.state = {
      stock,
      waste: [],
      foundations: [[], [], [], []],
      tableau,
      drawCount,
      moves: 0,
      seed,
    };
  }

  /** Copie profonde de l'etat courant : le rendu peut la garder sans risque. */
  get view(): GameState {
    return structuredClone(this.state);
  }

  get won(): boolean {
    return isWon(this.state);
  }

  get autoCompletable(): boolean {
    return canAutoComplete(this.state);
  }

  get canUndo(): boolean {
    return this.history.length > 0;
  }

  // ------------------------------------------------------------- actions

  /**
   * Clic sur la pioche : tire jusqu'a drawCount cartes vers la defausse, ou
   * recycle la defausse (retournee) si la pioche est vide. Refuse seulement
   * si pioche ET defausse sont vides.
   */
  draw(): ActionResult {
    const s = this.state;
    if (s.stock.length === 0 && s.waste.length === 0) return REFUSED;
    this.pushHistory();

    if (s.stock.length === 0) {
      // Recyclage : la defausse, retournee, redevient la pioche (nombre de
      // passages illimite en mode detente, cahier des charges §2).
      s.stock = s.waste.reverse().map((card) => ({ ...card, faceUp: false }));
      s.waste = [];
      s.moves++;
      return accepted({ recycled: true });
    }

    const count = Math.min(s.drawCount, s.stock.length);
    for (let i = 0; i < count; i++) {
      const card = s.stock.pop()!;
      card.faceUp = true;
      s.waste.push(card);
    }
    s.moves++;
    return accepted({ drawn: count });
  }

  /** Deplace une sequence face visible du tableau (a partir de `index`) vers une autre colonne. */
  moveTableauRun(fromCol: number, index: number, toCol: number): ActionResult {
    const s = this.state;
    const from = s.tableau[fromCol];
    const to = s.tableau[toCol];
    if (!from || !to || fromCol === toCol) return REFUSED;
    if (!canPickUpRun(from, index)) return REFUSED;
    const moving = from[index]!;
    if (!canMoveToTableau(moving, to)) return REFUSED;

    this.pushHistory();
    const run = from.splice(index);
    to.push(...run);
    const flipped = this.flipTop(fromCol);
    s.moves++;
    return accepted({ flipped });
  }

  /** Sommet de la defausse vers une colonne du tableau. */
  moveWasteToTableau(toCol: number): ActionResult {
    const s = this.state;
    const to = s.tableau[toCol];
    const card = s.waste[s.waste.length - 1];
    if (!to || !card || !canMoveToTableau(card, to)) return REFUSED;

    this.pushHistory();
    to.push(s.waste.pop()!);
    s.moves++;
    return accepted();
  }

  /** Carte du sommet (defausse ou colonne) vers sa fondation. */
  moveToFoundation(source: MoveSource): ActionResult {
    // Une carte deja sur une fondation n'a nulle part ou aller "vers sa
    // fondation" : refuser avant pushHistory (sinon on empilerait une
    // entree d'annulation fantome qui ne changerait jamais rien).
    if (source.type === "foundation") return REFUSED;
    const s = this.state;
    const card = this.topOf(source);
    if (!card) return REFUSED;
    const fIndex = foundationIndex(card.suit);
    if (!canMoveToFoundation(card, s.foundations[fIndex]!, card.suit)) return REFUSED;

    this.pushHistory();
    let flipped = false;
    if (source.type === "waste") {
      s.foundations[fIndex]!.push(s.waste.pop()!);
    } else {
      s.foundations[fIndex]!.push(s.tableau[source.col]!.pop()!);
      flipped = this.flipTop(source.col);
    }
    s.moves++;
    return accepted({ flipped, toFoundation: true });
  }

  /** Sommet d'une fondation vers le tableau (autorise par les regles, -pratique pour se debloquer). */
  moveFoundationToTableau(suit: Suit, toCol: number): ActionResult {
    const s = this.state;
    const pile = s.foundations[foundationIndex(suit)]!;
    const card = pile[pile.length - 1];
    const to = s.tableau[toCol];
    if (!card || !to || !canMoveToTableau(card, to)) return REFUSED;

    this.pushHistory();
    to.push(pile.pop()!);
    s.moves++;
    return accepted();
  }

  /**
   * Resout la destination d'un double-clic/double-tap sur une carte : essaie
   * d'abord sa fondation (le geste le plus utile, cf. cahier des charges §6),
   * puis, si elle n'est pas jouable la, cherche la premiere colonne du
   * tableau qui peut l'accueillir (elle ou toute la sequence commencant a
   * elle). Refuse (ActionResult.ok = false) si aucune destination n'existe.
   */
  resolveDoubleClick(source: MoveSource): ActionResult {
    const toFoundation = this.moveToFoundation(source);
    if (toFoundation.ok) return toFoundation;

    for (let col = 0; col < this.state.tableau.length; col++) {
      if (source.type === "tableau" && source.col === col) continue;
      const result =
        source.type === "waste"
          ? this.moveWasteToTableau(col)
          : source.type === "tableau"
            ? this.moveTableauRun(source.col, source.index, col)
            : this.moveFoundationToTableau(source.suit, col);
      if (result.ok) return result;
    }
    return REFUSED;
  }

  /** Annule le dernier coup (pile d'historique complete, undo illimite). */
  undo(): boolean {
    const previous = this.history.pop();
    if (!previous) return false;
    this.state = previous;
    return true;
  }

  // ------------------------------------------------------------- interne

  private topOf(source: MoveSource): Card | null {
    const s = this.state;
    if (source.type === "waste") return s.waste[s.waste.length - 1] ?? null;
    if (source.type === "tableau") {
      const pile = s.tableau[source.col];
      if (!pile || source.index !== pile.length - 1) return null;
      return pile[pile.length - 1] ?? null;
    }
    const pile = s.foundations[foundationIndex(source.suit)]!;
    return pile[pile.length - 1] ?? null;
  }

  /** Retourne face visible la nouvelle carte du sommet d'une colonne, s'il y a lieu. */
  private flipTop(col: number): boolean {
    const pile = this.state.tableau[col]!;
    const top = pile[pile.length - 1];
    if (top && !top.faceUp) {
      top.faceUp = true;
      return true;
    }
    return false;
  }

  private pushHistory(): void {
    this.history.push(structuredClone(this.state));
  }
}
